import { Question } from 'app/models/types';
import { FieldNames } from 'app/models/fields/names';
import { SHARED } from 'app/models/questions/shared';
import { TextInput } from 'app/components/FormFields/TextInput';
import { Currency } from 'app/components/FormFields/Currency';
import { SwitchToggle } from 'app/components/FormFields/SwitchToggle';
import { Address } from 'app/components/FormFields/Address';
import { State } from 'app/components/FormFields/State';
import { ZipCode } from 'app/components/FormFields/ZipCode';
import { Select } from 'app/components/FormFields/Select';
import { SingleDate } from 'app/components/FormFields/SingleDate';
import { SingleFutureDate } from 'app/components/FormFields/SingleFutureDate';
import { SinglePastDate } from 'app/components/FormFields/SinglePastDate';
import { Email } from 'app/components/FormFields/Email';
import { BusinessName } from 'app/components/FormFields/BusinessName';
import { Phone } from 'app/components/FormFields/Phone';
import { Name } from 'app/components/FormFields/Name';
import { EquipmentListComponent } from 'app/components/FormFieldArrays/EquipmentList';
import { TextArea } from 'app/components/FormFields/TextArea';
import { NAME_SUFFIX_OPTIONS } from 'app/models/options/options';
import {
  CommercialEquipmentCondition,
  CommercialPaymentFrequency,
  CommercialPaymentTiming,
} from '@lenderful/domain';

const EQUIPMENT_CONDITION_OPTIONS = Object.values(CommercialEquipmentCondition).map((value) => ({ title: value, value }));
const PAYMENT_FREQUENCY_OPTIONS   = Object.values(CommercialPaymentFrequency).map((value) => ({ title: value, value }));
const PAYMENT_TIMING_OPTIONS      = Object.values(CommercialPaymentTiming).map((value) => ({ title: value, value }));

export const getCommercialLeaseMunicipalQuestionByName = (name: string): Question => {
  return COMMERCIAL_LEASE_MUNICIPAL_QUESTIONS.find((question) => {
    return question.name === name;
  }) as Question;
};

export const getCommercialLeaseMunicipalQuestionsByName = (names: string[] = []): Question[] => {
  return names.map(getCommercialLeaseMunicipalQuestionByName);
};

export const COMMERCIAL_LEASE_MUNICIPAL_QUESTIONS: Question[] = [
  // ── Lessee Information ──────────────────────────────────────────────────────
  {
    id       : 1001,
    component: BusinessName,
    label    : 'Lessee Name',
    name     : FieldNames.commercialBusinessName,
  },
  {
    id       : 1002,
    component: SinglePastDate,
    label    : 'Lessee Date Established',
    name     : FieldNames.commercialBusinessStartDate,
  },
  {
    id       : 1003,
    component: Address,
    label    : 'Street Address',
    name     : FieldNames.commercialStreet,
    title    : 'Where is the lessee located?',
  },
  {
    id        : 1004,
    component : Address,
    isOptional: true,
    label     : 'Street Address 2',
    name      : FieldNames.commercialStreet2,
  },
  {
    id       : 1005,
    component: Address,
    label    : 'City',
    name     : FieldNames.commercialCity,
  },
  {
    id       : 1006,
    component: State,
    label    : 'State',
    name     : FieldNames.commercialState,
  },
  {
    id       : 1007,
    component: ZipCode,
    label    : 'Zip Code',
    name     : FieldNames.commercialZip,
  },
  {
    id       : 1008,
    component: Address,
    label    : 'Billing Street Address',
    name     : FieldNames.lesseeBillingStreet,
    title    : 'Tell us about the lessee billing address',
  },
  {
    id        : 1009,
    component : Address,
    isOptional: true,
    label     : 'Billing Street Address 2',
    name      : FieldNames.lesseeBillingStreet2,
  },
  {
    id       : 1010,
    component: Address,
    label    : 'Billing City',
    name     : FieldNames.lesseeBillingCity,
  },
  {
    id       : 1011,
    component: State,
    label    : 'Billing State',
    name     : FieldNames.lesseeBillingState,
  },
  {
    id       : 1012,
    component: ZipCode,
    label    : 'Billing Zip Code',
    name     : FieldNames.lesseeBillingZip,
  },
  {
    id       : 1013,
    component: TextInput,
    label    : 'Tax ID#',
    name     : FieldNames.lesseeTaxId,
  },
  {
    id        : 1014,
    component : Name,
    label     : 'First Name',
    name      : FieldNames.lesseeContactFirstName,
  },
  {
    id         : 1085,
    component  : Name,
    label      : 'Middle Name',
    name       : FieldNames.lesseeContactMiddleName,
    isOptional : true,
  },
  {
    id        : 1086,
    component : Name,
    label     : 'Last Name',
    name      : FieldNames.lesseeContactLastName,
  },
  {
    id         : 1087,
    component  : Select,
    label      : 'Suffix',
    name       : FieldNames.lesseeContactSuffixName,
    options    : NAME_SUFFIX_OPTIONS,
    isOptional : true,
  },
  {
    id       : 1015,
    component: TextInput,
    label    : 'Contact Title',
    name     : FieldNames.lesseeContactTitle,
  },
  {
    ...SHARED[FieldNames.phone],
    id  : 1016,
    name: FieldNames.commercialPhone,
  },
  {
    id       : 1017,
    component: Email,
    label    : 'Lessee Email',
    name     : FieldNames.commercialBusinessEmail,
  },
  // ── Equipment ───────────────────────────────────────────────────────────────
  {
    id       : 1018,
    component: TextInput,
    label    : 'Equipment Vendor',
    name     : FieldNames.vendorName,
  },
  {
    id       : 1019,
    component: Address,
    label    : 'Street Address',
    name     : FieldNames.vendorStreet,
  },
  {
    id        : 1020,
    component : Address,
    isOptional: true,
    label     : 'Street Address 2',
    name      : FieldNames.vendorStreet2,
  },
  {
    id       : 1021,
    component: Address,
    label    : 'City',
    name     : FieldNames.vendorCity,
  },
  {
    id       : 1022,
    component: State,
    label    : 'State',
    name     : FieldNames.vendorState,
  },
  {
    id       : 1023,
    component: ZipCode,
    label    : 'Zip Code',
    name     : FieldNames.vendorZip,
  },
  {
    id       : 1024,
    component: Phone,
    label    : 'Phone',
    name     : FieldNames.vendorPhone,
  },
  {
    id       : 1025,
    component: TextInput,
    label    : 'Equipment Description',
    name     : FieldNames.commercialEquipmentDescription,
  },
  {
    id       : 1026,
    component: SingleFutureDate,
    label    : 'Equipment Delivery Date',
    name     : FieldNames.equipmentDeliveryDate,
  },
  {
    id       : 1027,
    component: Select,
    label    : 'Equipment Condition',
    name     : FieldNames.equipmentCondition,
    options  : EQUIPMENT_CONDITION_OPTIONS,
  },
  {
    id       : 1028,
    component: TextInput,
    label    : 'How old is existing equipment?',
    name     : FieldNames.existingEquipmentAge,
    showIf   : [(values) => values[FieldNames.equipmentCondition] === 'Replacement'],
  },
  {
    id       : 1029,
    component: TextInput,
    label    : 'Why addition?',
    name     : FieldNames.equipmentAdditionReason,
    showIf   : [(values) => values[FieldNames.equipmentCondition] === 'Addition'],
  },
  {
    id       : 1030,
    component: Currency,
    label    : 'Equipment Cost',
    name     : FieldNames.equipmentCost,
  },
  // ── Terms of Sale ───────────────────────────────────────────────────────────
  {
    id       : 1031,
    component: Currency,
    label    : 'Total Equipment Cost',
    name     : FieldNames.totalEquipmentCost,
  },
  {
    id       : 1032,
    component: Currency,
    label    : 'Trade-In Amount',
    name     : FieldNames.tradeInAmount,
  },
  {
    id       : 1033,
    component: Currency,
    label    : 'Cash Down',
    name     : FieldNames.cashDown,
  },
  {
    id       : 1034,
    component: Currency,
    label    : 'Rental Credit',
    name     : FieldNames.rentalCredit,
  },
  {
    id       : 1035,
    component: Currency,
    label    : 'Amount to Finance',
    name     : FieldNames.amountToFinance,
  },
  {
    id       : 1036,
    component: Select,
    label    : 'Payment Frequency',
    name     : FieldNames.paymentFrequency,
    options  : PAYMENT_FREQUENCY_OPTIONS,
  },
  {
    id       : 1037,
    component: Select,
    label    : 'First Payment Date',
    name     : FieldNames.firstPaymentTiming,
    options  : PAYMENT_TIMING_OPTIONS,
  },
  {
    id       : 1038,
    component: SingleDate,
    label    : 'Preferred Payment Date',
    name     : FieldNames.preferredPaymentDate,
  },
  {
    id       : 1039,
    component: SingleFutureDate,
    label    : 'Expected Vendor Payment Date',
    name     : FieldNames.expectedVendorPaymentDate,
  },
  // ── Essential Use / Source of Funds ─────────────────────────────────────────
  {
    id       : 1040,
    component: TextArea,
    paragraph: <>Please explain the use of the equipment and the primary department that uses it:</>,
    label    : 'Explain',
    name     : FieldNames.equipmentUseDepartment,
  },
  {
    id       : 1041,
    component: TextInput,
    label    : 'Which fund will make lease payments?',
    name     : FieldNames.leaseFundSource,
  },
  {
    id       : 1042,
    component: SwitchToggle,
    label    : 'Will grant or loans be used to make lease payments?',
    name     : FieldNames.grantsOrLoansForLease,
  },
  {
    id       : 1043,
    component: SwitchToggle,
    label    : 'Expecting to issue or designate more than $10M in tax exempt obligations this year?',
    name     : FieldNames.taxExemptObligationsOver10M,
  },
  {
    id       : 1044,
    component: TextArea,
    paragraph: <>What additional leasing/financing is anticipated over the term of this lease?</>,
    label    : 'Explain',
    name     : FieldNames.additionalLeasingAnticipated,
  },
  {
    id       : 1045,
    component: SwitchToggle,
    label    : 'Any past non-appropriations?',
    name     : FieldNames.pastNonAppropriations,
  },
  {
    id       : 1046,
    component: TextArea,
    label    : 'Explain',
    name     : FieldNames.pastNonAppropriationsExplanation,
    showIf   : [(values) => values[FieldNames.pastNonAppropriations] === true],
  },
  {
    id       : 1047,
    component: TextInput,
    label    : 'Current Debt Rating',
    name     : FieldNames.currentDebtRating,
  },
  {
    id       : 1048,
    component: TextInput,
    label    : 'Primary Bank Name',
    name     : FieldNames.primaryBankName,
  },
  {
    id       : 1049,
    component: Address,
    label    : 'Street Address',
    name     : FieldNames.primaryBankStreet,
  },
  {
    id        : 1050,
    component : Address,
    isOptional: true,
    label     : 'Street Address 2',
    name      : FieldNames.primaryBankStreet2,
  },
  {
    id       : 1051,
    component: Address,
    label    : 'City',
    name     : FieldNames.primaryBankCity,
  },
  {
    id       : 1052,
    component: State,
    label    : 'State',
    name     : FieldNames.primaryBankState,
  },
  {
    id       : 1053,
    component: ZipCode,
    label    : 'Zip Code',
    name     : FieldNames.primaryBankZip,
  },
  {
    id       : 1054,
    component: TextInput,
    label    : 'Officer Name',
    name     : FieldNames.primaryBankOfficerName,
  },
  {
    id       : 1055,
    component: Phone,
    label    : 'Officer Phone',
    name     : FieldNames.primaryBankOfficerPhone,
  },
  {
    id       : 1056,
    component: TextInput,
    name     : FieldNames.bankOrLessorName,
    label    : 'Lessor Name',
  },
  {
    id       : 1057,
    component: Address,
    label    : 'Street Address',
    name     : FieldNames.bankOrLessorStreet,
  },
  {
    id        : 1058,
    component : Address,
    isOptional: true,
    label     : 'Street Address 2',
    name      : FieldNames.bankOrLessorStreet2,
  },
  {
    id       : 1059,
    component: Address,
    label    : 'City',
    name     : FieldNames.bankOrLessorCity,
  },
  {
    id       : 1060,
    component: State,
    label    : 'State',
    name     : FieldNames.bankOrLessorState,
  },
  {
    id       : 1061,
    component: ZipCode,
    label    : 'Zip Code',
    name     : FieldNames.bankOrLessorZip,
  },
  {
    id       : 1062,
    component: Phone,
    label    : 'Phone',
    name     : FieldNames.bankOrLessorPhone,
  },
  {
    id       : 1063,
    component: TextInput,
    label    : 'Contact',
    name     : FieldNames.bankOrLessorContact,
  },
  {
    id       : 1064,
    component: TextInput,
    label    : 'Account #',
    name     : FieldNames.bankOrLessorAccountNumber,
  },
  {
    id       : 1065,
    component: TextInput,
    label    : 'Law Firm Name',
    name     : FieldNames.lawFirmName,
  },
  {
    id       : 1066,
    component: Address,
    label    : 'Street Address',
    name     : FieldNames.lawFirmStreet,
  },
  {
    id        : 1067,
    component : Address,
    isOptional: true,
    label     : 'Street Address 2',
    name      : FieldNames.lawFirmStreet2,
  },
  {
    id       : 1068,
    component: Address,
    label    : 'City',
    name     : FieldNames.lawFirmCity,
  },
  {
    id       : 1069,
    component: State,
    label    : 'State',
    name     : FieldNames.lawFirmState,
  },
  {
    id       : 1070,
    component: ZipCode,
    label    : 'Zip Code',
    name     : FieldNames.lawFirmZip,
  },
  {
    id       : 1071,
    component: TextInput,
    label    : 'Attorney Name',
    name     : FieldNames.attorneyName,
  },
  {
    id       : 1072,
    component: Phone,
    label    : 'Phone',
    name     : FieldNames.lawFirmPhone,
  },
  {
    id       : 1073,
    component: TextInput,
    label    : 'Insurance Company Name',
    name     : FieldNames.insuranceCompanyName,
  },
  {
    id       : 1074,
    component: Address,
    label    : 'Street Address',
    name     : FieldNames.insuranceCompanyStreet,
    title    : 'Where is the insurance company located?',
  },
  {
    id        : 1075,
    component : Address,
    isOptional: true,
    label     : 'Street Address 2',
    name      : FieldNames.insuranceCompanyStreet2,
  },
  {
    id       : 1076,
    component: Address,
    label    : 'City',
    name     : FieldNames.insuranceCompanyCity,
  },
  {
    id       : 1077,
    component: State,
    label    : 'State',
    name     : FieldNames.insuranceCompanyState,
  },
  {
    id       : 1078,
    component: ZipCode,
    label    : 'Zip Code',
    name     : FieldNames.insuranceCompanyZip,
  },
  {
    id       : 1079,
    component: Name,
    label    : 'First Name',
    name     : FieldNames.insuranceAgentFirstName,
    title    : 'Tell us about the insurance agent',
  },
  {
    id       : 1080,
    component: Phone,
    label    : 'Insurance Agent Phone',
    name     : FieldNames.insuranceAgentPhone,
  },
  {
    id       : 1081,
    component: EquipmentListComponent,
    name     : FieldNames.commercialEquipmentList,
  },
  {
    id        : 1082,
    component : Name,
    isOptional: true,
    label     : 'Middle Name',
    name      : FieldNames.insuranceAgentMiddleName,
  },
  {
    id       : 1083,
    component: Name,
    label    : 'Last Name',
    name     : FieldNames.insuranceAgentLastName,
  },
  {
    id        : 1084,
    component : Select,
    isOptional: true,
    label     : 'Suffix',
    name      : FieldNames.insuranceAgentSuffixName,
    options   : NAME_SUFFIX_OPTIONS,
  },
  {
    id       : 1085,
    component: Email,
    label    : 'Insurance Agent Email',
    name     : FieldNames.insuranceAgentEmail,
  },
];
